import React, { useState } from "react";
import {
  ArrowLeft, MoreVertical, FileText, Receipt, Share2, Download, User, Wrench,
  ChevronRight, Calendar, Percent, Plus, Trash2, Pencil, Check, PenLine, Mail,
  ArrowRightLeft, Link2, Loader2, Crown, Lock, X, Package, ListPlus,
  MapPin, Users, Send, Save,
} from "lucide-react";
import { toast } from "sonner";
import { paymentInfo, estimateStatusLabel } from "../Estimates";
import MobileBottomNav from "../../components/mobile/MobileBottomNav";

const BRAND_BLUE = "#0077CC";
const BRAND_SKY = "#66B2E6";
const INK = "#1A1A2E";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};

const Card = ({ children, testid, className = "" }) => (
  <div data-testid={testid} className={`bg-white rounded-2xl shadow-sm p-4 ${className}`}>{children}</div>
);

/**
 * Mobile Estimate/Invoice detail — visual-only redesign matching a provided
 * mockup. All data, handlers and business rules are owned by <DocDetail> in
 * ../Estimates.jsx and simply passed down as props; nothing here mutates
 * data on its own beyond calling those same handlers.
 */
export default function MobileDocDetail({
  kind, doc, isEstimate, customer, linkedJob,
  draft, mark, items, pricedById, totals, effectivePlan,
  editing, locked, isHistory, canEdit, isDraft,
  busy, dirty, downloading,
  addLine, updateLine, removeLine, openPicker,
  onSaveClick, openPresent, openEmail, convert, viewInvoice,
  downloadPdf, del, copyPayLink, copyAcceptLink, payLink, acceptLink,
  onClose, onOpenCustomer, onOpenJob, user, mobileBackLabel,
}) {
  const [docMenuOpen, setDocMenuOpen] = useState(false);
  const [lineMenuId, setLineMenuId] = useState(null);
  const [editLineId, setEditLineId] = useState(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const info = paymentInfo(doc, kind);

  const share = async () => {
    const link = isEstimate ? acceptLink : payLink;
    if (!link) { toast.error("No shareable link yet"); return; }
    if (navigator.share) {
      try { await navigator.share({ title: doc.id, url: link }); return; } catch { /* user cancelled */ }
    }
    if (isEstimate) await copyAcceptLink(); else await copyPayLink();
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="mv relative w-full h-full flex flex-col overflow-hidden"
      style={{ background: "linear-gradient(to bottom, #1484F6, #FAFBFC)" }}
      data-testid="mobile-doc-detail"
    >
      {/* Header — standard app blue gradient */}
      <div className="relative shrink-0 px-3 flex items-center justify-center" style={{ background: `linear-gradient(to bottom, ${BRAND_BLUE}, ${BRAND_SKY})`, paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)", paddingBottom: "0.75rem" }}>
        <button onClick={onClose} data-testid="mobile-doc-back" className="absolute left-3 flex items-center gap-1 text-white font-semibold text-[14px] active:opacity-70" style={{ top: "calc(env(safe-area-inset-top) + 0.75rem)" }}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2.4} /> {mobileBackLabel || (isEstimate ? "Estimates" : "Invoices")}
        </button>
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/mobile-header-logo`} alt="Service Guru Pro" className="h-14 w-14 object-contain" data-testid="mobile-doc-logo" />
        <div className="absolute right-2" style={{ top: "calc(env(safe-area-inset-top) + 0.5rem)" }}>
          <button onClick={() => setDocMenuOpen((v) => !v)} data-testid="mobile-doc-menu-btn" aria-label="More" className="h-9 w-9 rounded-full flex items-center justify-center text-white active:bg-white/15">
            <MoreVertical className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Summary card */}
        <Card testid="mobile-doc-summary-card">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#DCEBFF" }}>
                {isEstimate ? <FileText className="h-5 w-5" style={{ color: BRAND_BLUE }} /> : <Receipt className="h-5 w-5" style={{ color: BRAND_BLUE }} />}
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{isEstimate ? "Estimate" : "Invoice"}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[17px] font-bold whitespace-nowrap" style={{ color: INK }} data-testid="mobile-doc-id">{doc.id}</span>
                  <span className="text-[11px] font-bold rounded-full px-2.5 py-0.5 whitespace-nowrap shrink-0" style={{ backgroundColor: "#EBF3FF", color: BRAND_BLUE }} data-testid="mobile-doc-status">{estimateStatusLabel(doc.status)}</span>
                </div>
                <div className="text-[12.5px] text-slate-500 truncate mt-0.5" data-testid="mobile-doc-customer-name">{doc.customer}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={share} data-testid="mobile-doc-share-btn" className="flex flex-col items-center gap-1 active:opacity-60">
                <span className="h-9 w-9 rounded-xl border border-slate-200 flex items-center justify-center">
                  <Share2 className="h-4 w-4" color="#4B5563" />
                </span>
                <span className="text-[10px] font-semibold text-slate-500">Share</span>
              </button>
              <button onClick={downloadPdf} disabled={downloading} data-testid="mobile-doc-pdf-btn" className="flex flex-col items-center gap-1 active:opacity-60">
                <span className="h-9 w-9 rounded-xl border border-slate-200 flex items-center justify-center">
                  {downloading ? <Loader2 className="h-4 w-4 animate-spin" color="#4B5563" /> : <Download className="h-4 w-4" color="#4B5563" />}
                </span>
                <span className="text-[10px] font-semibold text-slate-500">PDF</span>
              </button>
              <button onClick={() => setDocMenuOpen((v) => !v)} data-testid="mobile-doc-more-btn" className="flex flex-col items-center gap-1 active:opacity-60">
                <span className="h-9 w-9 rounded-xl border border-slate-200 flex items-center justify-center">
                  <MoreVertical className="h-4 w-4" color="#4B5563" />
                </span>
                <span className="text-[10px] font-semibold text-slate-500">More</span>
              </button>
            </div>
          </div>
        </Card>

        {/* Contextual status date — directly below the EST number */}
        {isEstimate && (doc.status === "Sent" || doc.status === "Draft") && (
          <div className="px-1" data-testid="mobile-doc-status-date">
            <span className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
              {doc.status === "Sent" ? "Estimate Sent Date" : "Draft Date"}
            </span>{" "}
            <span className="text-[12.5px] font-bold" style={{ color: INK }}>
              {fmtDate(doc.status === "Sent" ? doc.lastSentAt : doc.issuedAt)}
            </span>
          </div>
        )}

        {/* Customer / Linked Job */}
        <Card testid="mobile-doc-customerjob-card" className="!p-0">
          <div className="grid grid-cols-2">
            <button onClick={() => onOpenCustomer && customer && onOpenCustomer(customer.id)} data-testid="mobile-doc-customer-card" className="text-left p-3.5 pr-3" disabled={!customer}>
              <div className="text-[10px] font-semibold uppercase text-slate-400 mb-1.5">Customer</div>
              <div className="flex items-center justify-between gap-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <User className="h-3.5 w-3.5 shrink-0" style={{ color: BRAND_BLUE }} />
                  <span className="text-[13.5px] font-bold truncate" style={{ color: INK }}>{customer?.name || doc.customer || "—"}</span>
                </div>
                {customer && <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />}
              </div>
              <div className="flex items-start gap-1.5 mt-2">
                <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-400" />
                <span className="text-[12px] text-slate-500 text-left">{customer ? `${customer.address}, ${customer.city}` : "—"}</span>
              </div>
            </button>
            <button onClick={() => linkedJob && onOpenJob && onOpenJob(linkedJob.id)} data-testid="mobile-doc-job-card" className="text-left p-3.5 pl-3 border-l border-slate-100" disabled={!linkedJob}>
              <div className="text-[10px] font-semibold uppercase text-slate-400 mb-1.5">Linked Job</div>
              <div className="flex items-center justify-between gap-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Wrench className="h-3.5 w-3.5 shrink-0" style={{ color: BRAND_BLUE }} />
                  <span className="text-[13.5px] font-bold truncate" style={{ color: INK }}>{linkedJob?.type || "None"}</span>
                </div>
                {linkedJob && <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />}
              </div>
              <div className="flex items-center justify-between gap-1 mt-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Calendar className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="text-[12.5px] font-bold truncate" style={{ color: INK }}>{linkedJob ? fmtDate(linkedJob.start) : "—"}</span>
                </div>
                {linkedJob && <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />}
              </div>
            </button>
          </div>
        </Card>

        {/* Description */}
        <Card testid="mobile-doc-description-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[14px] font-bold" style={{ color: INK }}>Description</span>
            <button onClick={() => toast.message("Template library coming soon")} data-testid="mobile-doc-use-template" className="flex items-center gap-1 text-[12.5px] font-semibold" style={{ color: BRAND_BLUE }}>
              <FileText className="h-3.5 w-3.5" /> Use Template
            </button>
          </div>
          <textarea
            value={draft.jobSummary}
            onChange={(e) => mark({ jobSummary: e.target.value })}
            disabled={!canEdit}
            rows={3}
            placeholder="Summary of the visit…"
            data-testid="mobile-doc-description-input"
            className="w-full rounded-lg px-3 py-2.5 text-[13.5px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:opacity-70"
            style={{ backgroundColor: "#F3F4F6", color: INK }}
          />
        </Card>

        {effectivePlan && (
          <div className="rounded-xl border px-3 py-2 flex items-center gap-2" style={{ borderColor: `${effectivePlan.color}55`, background: `${effectivePlan.color}12` }} data-testid="mobile-doc-membership-banner">
            <Crown className="h-4 w-4 shrink-0" style={{ color: effectivePlan.color }} />
            <div className="text-[12px] text-slate-700"><strong style={{ color: effectivePlan.color }}>{effectivePlan.name}</strong> member discount <strong>{effectivePlan.discountPct}%</strong> applied</div>
          </div>
        )}
        {locked && (
          <div className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 flex items-center gap-2" data-testid="mobile-doc-locked-banner">
            <Lock className="h-4 w-4 shrink-0 text-violet-600" />
            <div className="text-[12px] text-slate-700">Converted to invoice <strong className="text-violet-700">{doc.convertedInvoiceId}</strong> — locked to keep your books accurate.</div>
          </div>
        )}

        {/* Line items */}
        <Card testid="mobile-doc-lineitems-card" className="!p-0 overflow-hidden">
          <div className="flex items-center justify-between p-4 pb-2">
            <span className="text-[14px] font-bold" style={{ color: INK }}>Line Items ({items.length})</span>
            {canEdit && (
              <button onClick={() => setAddMenuOpen(true)} data-testid="mobile-doc-add-item" className="text-[12.5px] font-bold text-white px-3.5 py-1.5 rounded-full active:scale-95 transition-transform" style={{ backgroundColor: BRAND_BLUE }}>
                + Add Item
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" style={{ minWidth: "480px" }}>
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide font-semibold text-white whitespace-nowrap" style={{ backgroundColor: "#1F3257" }}>
                  <th className="px-2.5 py-2">Description</th>
                  <th className="px-2 py-2 w-12 text-right">QTY</th>
                  <th className="px-2 py-2 w-16 text-right">Unit $</th>
                  <th className="px-2 py-2 w-10 text-center">Tax</th>
                  <th className="px-2 py-2 w-16 text-right">Total</th>
                  <th className="px-1 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-[12px] text-slate-400 italic">No line items yet</td></tr>
                ) : items.map((li) => {
                  const isEditingRow = editLineId === li.id;
                  const lineTotal = (Number(li.qty) || 0) * (Number(pricedById[li.id]?.memberUnitPrice ?? li.unitPrice) || 0);
                  const lineTaxAmt = li.taxable !== false ? lineTotal * ((Number(draft.taxRate) || 0) / 100) : 0;
                  const rowTotal = lineTotal + lineTaxAmt;
                  return (
                    <tr key={li.id} className="border-t border-slate-100 align-top">
                      <td className="px-2.5 py-2 align-top">
                        {isEditingRow && canEdit ? (
                          <textarea value={li.description} onChange={(e) => updateLine(li.id, { description: e.target.value })} rows={2} data-testid={`mobile-line-desc-${li.id}`} className="w-full min-w-[140px] px-1.5 py-1 rounded border border-slate-200 text-[12px] resize-y" />
                        ) : (
                          <span className="line-clamp-2" style={{ color: "#374151" }}>{li.description || <span className="italic text-slate-400">—</span>}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right align-top" style={{ color: "#5B697E" }}>
                        {isEditingRow && canEdit ? (
                          <input type="number" min={0} step={0.5} value={li.qty} onChange={(e) => updateLine(li.id, { qty: parseFloat(e.target.value) || 0 })} data-testid={`mobile-line-qty-${li.id}`} className="w-12 px-1 py-1 rounded border border-slate-200 text-[12px] text-right" />
                        ) : li.qty}
                      </td>
                      <td className="px-2 py-2 text-right align-top" style={{ color: "#5B697E" }}>
                        {isEditingRow && canEdit ? (
                          <input type="number" min={0} step={0.01} value={li.unitPrice} onChange={(e) => updateLine(li.id, { unitPrice: parseFloat(e.target.value) || 0 })} data-testid={`mobile-line-price-${li.id}`} className="w-16 px-1 py-1 rounded border border-slate-200 text-[12px] text-right" />
                        ) : money(li.unitPrice)}
                      </td>
                      <td className="px-2 py-2 text-right align-top" style={{ color: "#5B697E" }}>
                        {isEditingRow && canEdit ? (
                          <input type="checkbox" checked={li.taxable !== false} onChange={(e) => updateLine(li.id, { taxable: e.target.checked })} data-testid={`mobile-line-tax-${li.id}`} className="h-3.5 w-3.5 accent-blue-600" />
                        ) : money(lineTaxAmt)}
                      </td>
                      <td className="px-2 py-2 text-right align-top font-semibold whitespace-nowrap" style={{ color: "#374151" }}>{money(rowTotal)}</td>
                      <td className="px-1 py-2 align-top text-right" style={{ color: "#5B697E" }}>
                        <button
                          onClick={() => setLineMenuId(li.id)}
                          data-testid={`mobile-line-menu-${li.id}`}
                          aria-label="Row actions"
                          className="p-0"
                          style={{ color: "#5B697E", background: "none", border: "none", boxShadow: "none", padding: 0 }}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-4 pt-3 space-y-1.5 text-[13.5px] border-t border-slate-100">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-semibold" style={{ color: INK }}>{money(totals.subtotal)}</span></div>
            {totals.savings > 0 && (
              <div className="flex justify-between"><span className="text-violet-600">Membership savings</span><span className="font-semibold text-violet-700">−{money(totals.savings)}</span></div>
            )}
            {Number(draft.discount) > 0 && (
              <div className="flex justify-between"><span className="text-slate-500">Discount</span><span className="font-semibold text-rose-600">−{money(draft.discount)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-slate-500">Tax ({Number(draft.taxRate) || 0}%)</span><span className="font-semibold" style={{ color: INK }}>{money(totals.tax)}</span></div>
            <div className="flex justify-between pt-1.5 border-t border-slate-100 text-[16px]"><span className="font-bold" style={{ color: INK }}>Total</span><span className="font-bold" style={{ color: BRAND_BLUE }}>{money(totals.total)}</span></div>
            {(!isEstimate || info.paid > 0) && (
              <>
                <div className="flex justify-between text-[12.5px]"><span className="text-slate-500">Paid</span><span className="font-semibold text-emerald-700">{money(isEstimate ? info.paid : doc.amountPaid)}</span></div>
                <div className="flex justify-between text-[12.5px]"><span className="text-slate-500">Balance Remaining</span><span className="font-bold text-amber-700">{money(isEstimate ? info.balance : doc.balanceDue)}</span></div>
              </>
            )}
          </div>
        </Card>

        {/* Notes */}
        <Card testid="mobile-doc-notes-card">
          <span className="text-[14px] font-bold" style={{ color: INK }}>Notes (Internal)</span>
          <input
            value={draft.notes}
            onChange={(e) => mark({ notes: e.target.value })}
            disabled={!canEdit}
            placeholder="Add internal notes..."
            data-testid="mobile-doc-notes-input"
            className="w-full mt-2 rounded-lg px-3 py-2.5 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:opacity-70"
            style={{ backgroundColor: "#F3F4F6", color: INK }}
          />
        </Card>

        {/* Tax rate / Expiration */}
        <Card testid="mobile-doc-taxrate-expiry-card" className="!p-0">
          <div className="grid grid-cols-2">
            <div className="p-3.5 pr-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "#DCEBFF" }}>
                  <Percent className="h-3.5 w-3.5" style={{ color: BRAND_BLUE }} />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Tax Rate</span>
              </div>
              <div className="flex items-center justify-between gap-1">
                <input
                  type="number" min={0} step={0.1} value={draft.taxRate}
                  onChange={(e) => mark({ taxRate: e.target.value })}
                  disabled={!canEdit}
                  data-testid="mobile-doc-taxrate-input"
                  className="w-full bg-transparent text-[14px] font-bold focus:outline-none disabled:opacity-70"
                  style={{ color: INK }}
                />
                <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
              </div>
            </div>
            <div className="p-3.5 pl-3 border-l border-slate-100">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "#DCEBFF" }}>
                  <Calendar className="h-3.5 w-3.5" style={{ color: BRAND_BLUE }} />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{isEstimate ? "Expires" : "Due Date"}</span>
              </div>
              <div className="flex items-center justify-between gap-1">
                <input
                  type="date"
                  value={(isEstimate ? draft.expiresAt : draft.dueAt) ? (isEstimate ? draft.expiresAt : draft.dueAt).slice(0, 10) : ""}
                  onChange={(e) => mark(isEstimate ? { expiresAt: e.target.value } : { dueAt: e.target.value })}
                  disabled={locked}
                  data-testid="mobile-doc-expires-input"
                  className="w-full bg-transparent text-[14px] font-bold focus:outline-none disabled:opacity-70"
                  style={{ color: INK }}
                />
                <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
              </div>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="space-y-2.5 pt-1 pb-4">
          {canEdit && isEstimate && (
            <button onClick={openPresent} disabled={busy} data-testid="mobile-doc-present-btn" className="w-full h-12 rounded-full text-white text-[14.5px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60" style={{ backgroundColor: BRAND_BLUE }}>
              <Users className="h-4.5 w-4.5" /> Present to Customer
            </button>
          )}
          <button onClick={openEmail} disabled={busy} data-testid="mobile-doc-send-btn" className="w-full h-12 rounded-full bg-emerald-600 text-white text-[14.5px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
            <Send className="h-4.5 w-4.5" /> Send to Customer
          </button>
          <div className="grid grid-cols-2 gap-2.5">
            {canEdit ? (
              <button onClick={onSaveClick} disabled={(!dirty) || busy} data-testid="mobile-doc-savedraft-btn" className="h-12 rounded-full text-[14px] font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform disabled:opacity-60" style={{ backgroundColor: "#DCE8FB", color: BRAND_BLUE }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isDraft ? "Save as Draft" : "Save Changes"}
              </button>
            ) : (
              <button onClick={downloadPdf} disabled={downloading} data-testid="mobile-doc-download-btn" className="h-12 rounded-full text-[14px] font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform" style={{ backgroundColor: "#DCE8FB", color: BRAND_BLUE }}>
                <Download className="h-4 w-4" /> Download PDF
              </button>
            )}
            {isEstimate ? (
              locked ? (
                <button onClick={viewInvoice} disabled={busy} data-testid="mobile-doc-viewinvoice-btn" className="h-12 rounded-full text-white text-[14px] font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform" style={{ backgroundColor: "#7C3AED" }}>
                  <ArrowRightLeft className="h-4 w-4" /> {doc.convertedInvoiceId}
                </button>
              ) : doc.status === "Accepted" && !doc.convertedInvoiceId ? (
                <button onClick={convert} disabled={busy} data-testid="mobile-doc-convert-btn" className="h-12 rounded-full text-white text-[14px] font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform" style={{ backgroundColor: "#1E3A8A" }}>
                  <FileText className="h-4 w-4" /> Convert to Invoice
                </button>
              ) : (
                <button onClick={() => copyAcceptLink && copyAcceptLink()} data-testid="mobile-doc-acceptlink-btn" className="h-12 rounded-full text-white text-[12.5px] font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform px-1" style={{ backgroundColor: "#1E3A8A" }}>
                  <Link2 className="h-4 w-4 shrink-0" /> Copy Link
                </button>
              )
            ) : (
              <button onClick={() => copyPayLink && copyPayLink()} disabled={!(doc.balanceDue > 0)} data-testid="mobile-doc-paylink-btn" className="h-12 rounded-full text-white text-[12.5px] font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform disabled:opacity-50 px-1" style={{ backgroundColor: "#1E3A8A" }}>
                <Link2 className="h-4 w-4 shrink-0" /> Copy Link
              </button>
            )}
          </div>
        </div>

        {/* Spacer so action buttons clear the fixed bottom nav — a dedicated
            height div is used instead of container padding because the
            `.cc-theme .p-4 { padding: ... !important }` rule overrides any
            padding-bottom set on this scroll container. */}
        <div aria-hidden="true" style={{ height: "calc(5rem + 1rem + env(safe-area-inset-bottom))" }} />
      </div>

      {/* Add-item choice — fixed bottom sheet */}
      {addMenuOpen && (
        <>
          <div className="fixed inset-0 z-[59] bg-black/40" onClick={() => setAddMenuOpen(false)} data-testid="mobile-add-sheet-backdrop" />
          <div className="fixed left-0 right-0 bottom-0 z-[61] bg-white rounded-t-2xl shadow-2xl p-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]" data-testid="mobile-add-actions-sheet">
            <div className="h-1 w-10 rounded-full bg-slate-200 mx-auto my-2" />
            <button
              onClick={() => { setAddMenuOpen(false); openPicker && openPicker(); }}
              data-testid="mobile-add-from-pricebook"
              className="w-full flex items-center gap-3 px-4 py-3.5 text-[15px] font-semibold text-slate-700 active:bg-slate-50 rounded-xl"
            >
              <Package className="h-4.5 w-4.5" style={{ color: BRAND_BLUE }} /> Pick from Pricebook
            </button>
            <button
              onClick={() => { setAddMenuOpen(false); addLine(); }}
              data-testid="mobile-add-blank-line"
              className="w-full flex items-center gap-3 px-4 py-3.5 text-[15px] font-semibold text-slate-700 active:bg-slate-50 rounded-xl"
            >
              <ListPlus className="h-4.5 w-4.5" style={{ color: BRAND_BLUE }} /> Add Blank Line
            </button>
            <button onClick={() => setAddMenuOpen(false)} data-testid="mobile-add-sheet-cancel" className="w-full flex items-center justify-center gap-2 px-4 py-3.5 text-[15px] font-semibold text-slate-400 active:bg-slate-50 rounded-xl">
              <X className="h-4.5 w-4.5" /> Cancel
            </button>
          </div>
        </>
      )}

      {/* Line item actions — fixed bottom sheet (avoids clipping inside the
          horizontally-scrolling table) */}
      {lineMenuId && (
        <>
          <div className="fixed inset-0 z-[59] bg-black/40" onClick={() => setLineMenuId(null)} data-testid="mobile-line-sheet-backdrop" />
          <div className="fixed left-0 right-0 bottom-0 z-[61] bg-white rounded-t-2xl shadow-2xl p-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]" data-testid="mobile-line-actions-sheet">
            <div className="h-1 w-10 rounded-full bg-slate-200 mx-auto my-2" />
            {canEdit && (
              <button
                onClick={() => { setEditLineId(editLineId === lineMenuId ? null : lineMenuId); setLineMenuId(null); }}
                data-testid={`mobile-line-edit-${lineMenuId}`}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-[15px] font-semibold text-slate-700 active:bg-slate-50 rounded-xl"
              >
                {editLineId === lineMenuId ? <Check className="h-4.5 w-4.5" /> : <Pencil className="h-4.5 w-4.5" />} {editLineId === lineMenuId ? "Done editing" : "Edit line item"}
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => { removeLine(lineMenuId); setLineMenuId(null); }}
                data-testid={`mobile-line-remove-${lineMenuId}`}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-[15px] font-semibold text-rose-600 active:bg-rose-50 rounded-xl"
              >
                <Trash2 className="h-4.5 w-4.5" /> Remove line item
              </button>
            )}
            <button onClick={() => setLineMenuId(null)} data-testid="mobile-line-sheet-cancel" className="w-full flex items-center justify-center gap-2 px-4 py-3.5 text-[15px] font-semibold text-slate-400 active:bg-slate-50 rounded-xl">
              <X className="h-4.5 w-4.5" /> Cancel
            </button>
          </div>
        </>
      )}

      <MobileBottomNav />
    </div>
  );
}
